import { Fab, Grid, Hidden, useMediaQuery, useTheme } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { QuotationSummaryResponse } from 'data-layer-cr/dist/models';
import {
  BaseEmptyPage,
  PaddedContent,
  Tabs,
  colors,
  dimensions,
} from 'design-system';

// Models
import { FilterOption } from 'src/models/Filter';
import { createFilterCategory } from 'src/models/Filter/filterData';
import Profile from 'src/models/Profile';

// Redux
import { logout, refresProfile } from 'src/store/profile/actions';
import {
  cleanCopyQuotation,
  getQuotations,
  resetCreationFormValues,
  updateQuotationModalIsOpenValue,
} from 'src/store/quotation/actions';
import {
  setInitialProfileUnderstood,
  setProfileUnderstood,
} from 'src/store/notification/actions';
import { RootState } from 'src/store/rootReducer';

// hooks
import useInfiniteScroll from 'src/hooks/useInfiniteScroll';
import useQueryBuilder from 'src/hooks/useQueryBuilder';

// Network
import { GET_LIST } from 'src/store/quotation/requestIdentifiers';

// Components
import QuotationsList from '../Quotations/List';

import FilterDrawer from '../../FilterDrawer';
import CopyQuotation from './CopyQuotation';
import DashboardToolbar from './DashboardToolbar';
import MetricTab from './MetricTab';
import QuotationFormManager from './QuotationFormManager';
import SearchToolbarOnlyMobile from './SearchToolbarOnlyMobile';

// Enum
import { DisplayModes } from '../Quotations/List/QuotationsList';

// helpers
import { getMetricTabs, showSearchElements } from './dashboardHelpers';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    fab: {
      position: 'fixed',
      bottom: theme.spacing(4),
      right: theme.spacing(4),
      backgroundColor: colors.bright_turquoise,
      color: colors.white,
      '&:hover': {
        backgroundColor: colors.madison,
      },
    },
    searchClearIcon: {
      position: 'absolute',
      right: 10,
      bottom: 9,
    },
  }),
);

export const DashboardScreen = ({
  profile,
  newQuotationIsOpen,
  referenceQuotationList,
  referenceQuotationListTotal,
  refreshProfileAction,
  getQuotationsAction,
  resetCreationFormValuesAction,
  logoutAction,
  updateQuotationModalIsOpenValueAction,
  cleanCopyQuotationAction,
  userID,
  profileUnderstood,
  setProfileUnderstoodAction,
  setInitialProfileUnderstoodAction,
}: PropsFromRedux): JSX.Element => {
  useEffect(() => {
    refreshProfileAction();
  }, [refreshProfileAction]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const showProfileUnderstood = useFeatureIsOn('fwm-personal-link-remainder');

  const [currentPage, updateCurrentPage] = useState(0);
  const [searchVisible, toggleSearchVisible] = useState(false);
  const [searchText, updateSearchText] = useState('');
  const [filterDrawerIsOpen, toggleFilterDrawer] = useState(false);
  const [currentMode, setCurentMode] = useState(DisplayModes.TableMode);
  const filterCategories = useMemo(
    () => createFilterCategory(userID),
    [userID],
  );

  const {
    addFilter,
    removeFilter,
    singularCategories,
    multipleCategories,
    tagFilterList,
    queryString,
    resetFilters,
  } = useQueryBuilder({ filterCategories });

  /** Infinite Scroll Logic */
  const {
    setSearchQuery,
    isLoading: quotationListIsLoading,
    list: quotationList,
    ref: infiniteScrollAnchorRef,
  } = useInfiniteScroll<QuotationSummaryResponse>({
    step: currentMode === DisplayModes.CardMode ? 12 : 24,
    getReferenceList: getQuotationsAction,
    referenceList: referenceQuotationList,
    referenceTotal: referenceQuotationListTotal,
    requestIdentifier: GET_LIST,
    additionalFilters: queryString,
  });

  /**
   * Search logic is called via this debounce method.
   */
  const debouncedCallRef = useRef(
    debounce<(param: string) => void>((query) => {
      setSearchQuery(query);
    }, 300),
  ).current;
  useEffect(() => {
    debouncedCallRef(searchText);
  }, [debouncedCallRef, searchText]);

  useEffect(() => {
    const isProfileUnderstood = !showProfileUnderstood;
    setInitialProfileUnderstoodAction(isProfileUnderstood);
  }, [showProfileUnderstood, setInitialProfileUnderstoodAction]);

  function toggleVisibility(): void {
    const newVisibility = !searchVisible;
    if (newVisibility) {
      inputRef.current?.focus();
    }
    toggleSearchVisible(newVisibility);
  }

  function handleOpenNewQuotation(): void {
    cleanCopyQuotationAction();
    resetCreationFormValuesAction();
    updateQuotationModalIsOpenValueAction(true);
  }

  function handleCloseNewQuotation(): void {
    cleanCopyQuotationAction();
    resetCreationFormValuesAction();
    updateQuotationModalIsOpenValueAction(false);
  }

  function handleAddFilter(newFilter: FilterOption): void {
    addFilter(newFilter, newFilter.categoryId);
  }

  function handleRemoveFilter(filter: FilterOption): void {
    removeFilter(filter.id, filter.categoryId);
  }

  const handleSelectCurrentTab = (newTab: number): void => {
    updateCurrentPage(newTab);
    resetFilters();
  };

  // Pages
  const quotationListForm = (
    <QuotationsList
      tagFilterList={tagFilterList}
      handleRemoveFilter={handleRemoveFilter}
      data-testid="quotations-form"
      handleOpenNewQuotation={handleOpenNewQuotation}
      quotationList={quotationList}
      isLoading={quotationListIsLoading}
      anchorRef={infiniteScrollAnchorRef}
      currentMode={currentMode}
      setCurentMode={setCurentMode}
    />
  );

  const metricPage = (
    <MetricTab
      data-testid="metric-tab"
      onAddFilterDrawer={handleAddFilter}
      handleChangeDisplayMode={setCurentMode}
      handleSelectTab={updateCurrentPage}
    />
  );
  const pages = [quotationListForm, metricPage];

  // The logged user name
  const name = profile ? `${profile.Name} ${profile.LastName}` : '';

  return (
    <BaseEmptyPage padding={0}>
      <DashboardToolbar
        data-testid="toolbar"
        user={name}
        onSearchClick={toggleVisibility}
        searchString={searchText}
        onUpdateSearchString={updateSearchText}
        onLogoutClick={logoutAction}
        showFilterIcon={showSearchElements(currentPage)}
        showSearchInput={showSearchElements(currentPage)}
        onFilterClick={() => {
          toggleFilterDrawer(true);
        }}
        profileUnderstood={profileUnderstood}
        setProfileUnderstood={setProfileUnderstoodAction}
      />
      <FilterDrawer
        data-testid="filter-drawer"
        open={filterDrawerIsOpen}
        onClose={() => toggleFilterDrawer(false)}
        filterCatalog={filterCategories}
        singularCategoriesFilterMap={singularCategories}
        multipleCategoriesFilterMap={multipleCategories}
        onAddFilter={handleAddFilter}
        onRemoveFilter={handleRemoveFilter}
      />
      <Grid container direction="column">
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Tabs
              data-testid="tabs"
              tabs={getMetricTabs(isMobile)}
              backgroundColor={colors.cloud}
              currentTab={currentPage}
              onTabChange={handleSelectCurrentTab}
              useDesktopPadding
              desktopSidePadding={
                dimensions.desktopSidePadding + theme.spacing(2)
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={12} container>
          <Hidden mdUp>
            <SearchToolbarOnlyMobile
              inputRef={inputRef}
              data-testing="search-toolbar-only-mobile"
              searchText={searchText}
              searchVisible={searchVisible}
              toggleFilterDrawer={toggleFilterDrawer}
              updateSearchText={updateSearchText}
            />
          </Hidden>
        </Grid>
        <Grid item>
          <PaddedContent>{pages[currentPage]}</PaddedContent>
        </Grid>
      </Grid>

      {isMobile && (
        <Fab
          id="sales-web-button-new-quotation"
          data-testid="openFormNewQuotation"
          className={classes.fab}
          onClick={handleOpenNewQuotation}
        >
          <img src="/svg/new_document.svg" alt="new" width={28} />
        </Fab>
      )}
      <QuotationFormManager
        data-testid="quotation-form-manager"
        isOpen={newQuotationIsOpen}
        onBackdropClick={handleCloseNewQuotation}
      />
      <CopyQuotation data-testid="copy-quotation-component" />
    </BaseEmptyPage>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  profile?: Profile | null;
  newQuotationIsOpen: boolean;
  referenceQuotationList: QuotationSummaryResponse[];
  referenceQuotationListTotal: number;
  userID: string | undefined;
  profileUnderstood: boolean;
} => ({
  profile: state.profile.profile,
  newQuotationIsOpen: state.quotation.newQuotationModalIsOpen,
  referenceQuotationList: state.quotation.quotationList,
  userID: state.profile.profile?.IdUser,
  referenceQuotationListTotal: state.infiniteScroll.total,
  profileUnderstood: state.notification.profileUnderstood,
});

const reduxActions = {
  refreshProfileAction: refresProfile,
  getQuotationsAction: getQuotations,
  resetCreationFormValuesAction: resetCreationFormValues,
  logoutAction: logout,
  updateQuotationModalIsOpenValueAction: updateQuotationModalIsOpenValue,
  cleanCopyQuotationAction: cleanCopyQuotation,
  setProfileUnderstoodAction: setProfileUnderstood,
  setInitialProfileUnderstoodAction: setInitialProfileUnderstood,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardScreen);
